import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getSuperServiceableAreas, deleteSuperServiceableArea } from '../../actions/superServiceableArea';
import { Plus, Search, Trash2, MapPin, Eye, AlertTriangle, Activity, Map } from 'lucide-react';

const SuperServiceableAreas = ({ getSuperServiceableAreas, deleteSuperServiceableArea, superServiceableAreas, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [areaToDelete, setAreaToDelete] = useState(null);
    const [filteredAreas, setFilteredAreas] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        document.title = 'Super Serviceable Areas - Management';
        getSuperServiceableAreas();
    }, [getSuperServiceableAreas]);

    useEffect(() => {
        if (superServiceableAreas) {
            let filtered = superServiceableAreas.filter(area =>
                area.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                area.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                area.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                area.state?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (statusFilter !== 'all') {
                filtered = filtered.filter(area =>
                    statusFilter === 'active' ? area.isActive : !area.isActive
                );
            }

            setFilteredAreas(filtered);
        }
    }, [superServiceableAreas, searchTerm, statusFilter]);

    const handleDeleteClick = (area) => {
        setAreaToDelete(area);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (areaToDelete) {
            deleteSuperServiceableArea(areaToDelete._id);
            setShowDeleteModal(false);
            setAreaToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setAreaToDelete(null);
    };

    const getStatusColor = (isActive) => {
        return isActive ? 'success' : 'secondary';
    };

    const getStatusText = (isActive) => {
        return isActive ? 'Active' : 'Inactive';
    };

    if (loading && !superServiceableAreas) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading super serviceable areas...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="text-primary mb-1 fw-bold">
                                    <Map className="me-2" size={32} />
                                    Super Serviceable Areas
                                </h2>
                                <p className="text-muted mb-0">Manage extended geographic coverage areas served with advance notice</p>
                            </div>
                            <Link
                                to="/inventory/v1.2/super-serviceable-areas/new"
                                className="btn btn-primary btn-lg d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Add Super Serviceable Area
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="row mb-4">
                    <div className="col-md-8">
                        <div className="input-group">
                            <span className="input-group-text bg-light">
                                <Search size={18} />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, description, city, or state..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <select
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Total Areas</h5>
                                        <h3 className="mt-2 mb-0">{superServiceableAreas?.length || 0}</h3>
                                    </div>
                                    <Map size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Active Areas</h5>
                                        <h3 className="mt-2 mb-0">
                                            {superServiceableAreas?.filter(area => area.isActive).length || 0}
                                        </h3>
                                    </div>
                                    <Activity size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Inactive Areas</h5>
                                        <h3 className="mt-2 mb-0">
                                            {superServiceableAreas?.filter(area => !area.isActive).length || 0}
                                        </h3>
                                    </div>
                                    <AlertTriangle size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Filtered Results</h5>
                                        <h3 className="mt-2 mb-0">{filteredAreas?.length || 0}</h3>
                                    </div>
                                    <Search size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card shadow-sm">
                    <div className="card-header bg-light">
                        <h5 className="mb-0">Super Serviceable Areas List</h5>
                    </div>
                    <div className="card-body p-0">
                        {filteredAreas && filteredAreas.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Name</th>
                                            <th>Description</th>
                                            <th>Location</th>
                                            <th>Scheduling</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAreas.map((area) => (
                                            <tr key={area._id}>
                                                <td>
                                                    <div className="fw-bold text-primary">{area.name}</div>
                                                </td>
                                                <td>
                                                    <div className="text-muted">
                                                        {area.description ? (
                                                            area.description.length > 50
                                                                ? `${area.description.substring(0, 50)}...`
                                                                : area.description
                                                        ) : (
                                                            <em>No description</em>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <MapPin size={16} className="text-muted me-2" />
                                                        <div>
                                                            <div className="fw-semibold">{area.city}</div>
                                                            <small className="text-muted">{area.state}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>{area.minNoticeHours ?? 1}h min notice</div>
                                                    <small className="text-muted">{area.maxDeliveriesPerHour ?? 10} deliveries/hour</small>
                                                </td>
                                                <td>
                                                    <span className={`badge bg-${getStatusColor(area.isActive)}`}>
                                                        {getStatusText(area.isActive)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {new Date(area.createdAt).toLocaleDateString()}
                                                    </small>
                                                </td>
                                                <td>
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <Link
                                                            to={`/inventory/v1.2/super-serviceable-areas/${area._id}`}
                                                            className="btn btn-outline-info btn-sm"
                                                            title="View Details"
                                                        >
                                                            <Eye size={14} />
                                                        </Link>
                                                        <button
                                                            onClick={() => handleDeleteClick(area)}
                                                            className="btn btn-outline-danger btn-sm"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <MapPin size={64} className="text-muted mb-3" />
                                <h5 className="text-muted">No super serviceable areas found</h5>
                                <p className="text-muted mb-4">
                                    {searchTerm || statusFilter !== 'all'
                                        ? 'Try adjusting your search filters'
                                        : 'Create your first super serviceable area to get started'
                                    }
                                </p>
                                {(!searchTerm && statusFilter === 'all') && (
                                    <Link
                                        to="/inventory/v1.2/super-serviceable-areas/new"
                                        className="btn btn-primary"
                                    >
                                        <Plus size={16} className="me-2" />
                                        Add First Super Serviceable Area
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title">
                                    <AlertTriangle size={20} className="text-danger me-2" />
                                    Confirm Deletion
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={cancelDelete}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-0">
                                    Are you sure you want to delete the super serviceable area{' '}
                                    <strong>"{areaToDelete?.name}"</strong>?
                                </p>
                                <div className="alert alert-warning mt-3 mb-0">
                                    <small>
                                        <strong>Warning:</strong> This action cannot be undone and may affect
                                        associated scheduled deliveries.
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer border-0">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={cancelDelete}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={confirmDelete}
                                >
                                    <Trash2 size={16} className="me-2" />
                                    Delete Area
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

SuperServiceableAreas.propTypes = {
    getSuperServiceableAreas: PropTypes.func.isRequired,
    deleteSuperServiceableArea: PropTypes.func.isRequired,
    superServiceableAreas: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    superServiceableAreas: state.superServiceableArea.superServiceableAreas,
    loading: state.superServiceableArea.loading
});

export default connect(mapStateToProps, {
    getSuperServiceableAreas,
    deleteSuperServiceableArea
})(SuperServiceableAreas);
